import { FC, useMemo, useCallback, useEffect, useState, ReactElement } from 'react'
import { DefinedUseQueryResult } from '@tanstack/react-query'

import {
  Button,
  CrudModal,
  SafetyCaseActionForm,
  safetyCaseActionValuesToSafetyCaseActionAddUpsert,
  safetyCaseActionValuesToSafetyCaseActionUpdateUpsert,
  safetyCaseActionToSafetyCaseActionValues,
  ISafetyCaseActionValues,
} from '../../../components'
import SafetyCaseActionsCardsList from './safety-case-actions-cards-list'

import { useModal } from '../../../hooks'
import {
  useDeleteSafetyCaseActionMutation,
  useCreateSafetyCaseActionMutation,
  useUpdateSafetyCaseActionMutation,
} from '../../../services'
import { useProfile } from '../../../security'

import { ISafetyCase, ISafetyCaseAction } from '../../../interfaces'

type SafetyCaseActionsProps = {
  hasWriteAccess?: boolean
  safetyCaseQuery: DefinedUseQueryResult<ISafetyCase | undefined, Error>
  setHeaderControls: (headerControls?: ReactElement) => void
  safetyCaseActionsPagination: {
    pageSize: number
    pageIndex: number
  }
  setSafetyCaseActionsPagination: (values: { [key: string]: number }) => void
}

const SafetyCaseActions: FC<SafetyCaseActionsProps> = props => {
  const {
    hasWriteAccess,
    safetyCaseQuery,
    setHeaderControls,
    safetyCaseActionsPagination,
    setSafetyCaseActionsPagination,
  } = props

  const { airportId } = useProfile()

  const safetyCase = safetyCaseQuery?.data

  const safetyCaseActions = safetyCase?.actions

  const {
    isOpen: isSafetyCaseActionModalOpen,
    closeModal: closeSafetyCaseActionModal,
    openModal: openSafetyCaseActionModal,
  } = useModal()

  const [crudModalMode, setCrudModalMode] = useState<'create' | 'delete' | 'update'>()

  const handleOpenCreateSafetyCaseActionModal = useCallback(() => {
    setCrudModalMode('create')
    openSafetyCaseActionModal()
  }, [openSafetyCaseActionModal])

  const [deletingSafetyCaseAction, setDeletingSafetyCaseAction] = useState<ISafetyCaseAction>()

  const handleOpenDeleteSafetyCaseActionModal = useCallback(
    (safetyCaseAction: ISafetyCaseAction) => {
      setDeletingSafetyCaseAction(safetyCaseAction)
      setCrudModalMode('delete')
      openSafetyCaseActionModal()
    },
    [openSafetyCaseActionModal],
  )

  const [updatingSafetyCaseAction, setUpdatingSafetyCaseAction] = useState<ISafetyCaseAction>()

  const handleOpenUpdateSafetyCaseActionModal = useCallback(
    (safetyCaseAction: ISafetyCaseAction) => {
      setUpdatingSafetyCaseAction(safetyCaseAction)
      setCrudModalMode('update')
      openSafetyCaseActionModal()
    },
    [openSafetyCaseActionModal],
  )

  const headerControls = useMemo(
    () => <Button onClick={handleOpenCreateSafetyCaseActionModal}>Log New Action</Button>,
    [handleOpenCreateSafetyCaseActionModal],
  )

  useEffect(() => {
    setHeaderControls(headerControls)
    return () => setHeaderControls(undefined)
  }, [setHeaderControls, headerControls])

  const safetyCaseActionActionsButtonOptions = useMemo(
    () =>
      hasWriteAccess
        ? [
            {
              label: 'Update',
              handler: (safetyCaseAction: ISafetyCaseAction) =>
                handleOpenUpdateSafetyCaseActionModal(safetyCaseAction),
            },
            {
              label: 'Delete',
              handler: (safetyCaseAction: ISafetyCaseAction) =>
                handleOpenDeleteSafetyCaseActionModal(safetyCaseAction),
            },
          ]
        : undefined,
    [hasWriteAccess, handleOpenDeleteSafetyCaseActionModal, handleOpenUpdateSafetyCaseActionModal],
  )

  const onActivityChangeMutator = useCallback(async (args: any, state: any, mutate: any) => {
    mutate.changeValue(state, 'safetyCaseActionTypeId', () => args[0])
  }, [])

  const mutators = useMemo(() => ({ onActivityChange: onActivityChangeMutator }), [onActivityChangeMutator])

  if (!safetyCase) {
    return null
  }

  return (
    <>
      <CrudModal
        modalSettings={{
          isOpen: isSafetyCaseActionModalOpen,
          closeModal: closeSafetyCaseActionModal,
          mode: crudModalMode,
        }}
        createSettings={{
          title: 'Safety Case Action Taken',
          useCreateMutation: useCreateSafetyCaseActionMutation,
          formValuesToAddUpsert: (safetyCaseActionValues: ISafetyCaseActionValues) =>
            safetyCaseActionValuesToSafetyCaseActionAddUpsert(safetyCaseActionValues, {
              safetyCaseId: safetyCase.id,
              airportId,
            }),
          onCreateSuccessMessage: 'Action successfully created',
          onCreateErrorMessage: 'Something went wrong while creating action',
          onSuccessCallback: safetyCaseQuery?.refetch,
        }}
        updateSettings={{
          title: 'Safety Case Action Taken',
          useUpdateMutation: useUpdateSafetyCaseActionMutation,
          entityToFormValues: safetyCaseActionToSafetyCaseActionValues,
          formValuesToUpdateUpsert: (safetyCaseActionValues: ISafetyCaseActionValues) =>
            safetyCaseActionValuesToSafetyCaseActionUpdateUpsert(
              safetyCaseActionValues,
              updatingSafetyCaseAction,
            ),
          onUpdateSuccessMessage: 'Action successfully updated',
          onUpdateErrorMessage: 'Something went wrong while updating action',
          onSuccessCallback: safetyCaseQuery?.refetch,
          updatingItem: updatingSafetyCaseAction,
        }}
        deleteSettings={{
          title: 'Safety Case Action Taken',
          useDeleteMutation: useDeleteSafetyCaseActionMutation,
          onDeleteSuccessMessage: 'Action successfully deleted',
          onDeleteErrorMessage: 'Something went wrong while deleting action',
          onSuccessCallback: safetyCaseQuery?.refetch,
          deletingItem: deletingSafetyCaseAction,
        }}
        FormComponent={SafetyCaseActionForm}
        formName="safety-case-action"
        mutators={mutators}
      />

      <SafetyCaseActionsCardsList
        safetyCaseActions={safetyCaseActions}
        pagination={safetyCaseActionsPagination}
        setPagination={setSafetyCaseActionsPagination}
        actionsButtonOptions={safetyCaseActionActionsButtonOptions}
        isLoading={safetyCaseQuery.isFetching}
      />
    </>
  )
}

export default SafetyCaseActions
